import { defineStore } from "pinia"
import { HttpService } from "@/helpers/http-service"
import { CONSTANT } from "@/helpers/constant"
import type { JcConfiguratorFilter, JcFilterSourceType, JcOptionManufacturer } from "@/types/jcFilterSystem"

const httpService = new HttpService()

interface ListResponse<T> {
  data: { total: number; data: T[] }
}

interface ConfiguratorFilterCreatePayload {
  id?: string
  filterSourceType: JcFilterSourceType
  eecomJcFilterGroupId?: string | null
  position: number
  mandatory: boolean
  displayLabel?: string | null
}

interface ConfiguratorFilterUpdatePayload {
  filterSourceType?: JcFilterSourceType
  eecomJcFilterGroupId?: string | null
  position?: number
  mandatory?: boolean
  displayLabel?: string | null
}

// Aggregate write-shape per BRIEFING_BACKEND_UI: one row per currency, unique per setting.
// setContainerProductVersionId is filled server-side (Defaults::LIVE_VERSION) when omitted.
export interface SetPricePayload {
  id?: string
  currencyId: string
  // null = default row; a manufacturer id = per-manufacturer override.
  // currencyId is injected server-side from the sales-rep context; sending it is harmless.
  manufacturerId?: string | null
  setPriceGross: number
  setPriceNet: number
  setContainerProductId: string
  setContainerProductVersionId?: string | null
  linked?: boolean | null
  taxId?: string | null
}

// Tax options for the set-price tax selector / net-from-gross calculation.
// Served by the JC backend route GET /eecom-jersey-configurator/backend/tax → { total, data: [{ id, name, taxRate }] }.
export interface JcTaxOption {
  id: string
  name: string
  taxRate: number
}

interface OptionSettingPayload {
  eecomJcFilterOptionId: string
  // New per-role minimums (role -> minInt). Server normalizes: keeps positive
  // ints for valid roles, an empty map becomes null. Replaces min*Quantity —
  // the total minimum is the derived sum of these.
  minRoleQuantities?: Record<string, number> | null
  // Deprecated: no longer written by the UI. Retained for type compat with callers.
  minOrderQuantity?: number | null
  minPlayerQuantity?: number | null
  minGoalkeeperQuantity?: number | null
  mediaId?: string | null
  colorHexCode?: string | null
  setPrices?: SetPricePayload[]
  // Category-relation map filterGroupId → allowed optionIds. Endpoint is REPLACE, not merge — always
  // send the complete map. Only include this key when actually editing the relation: a missing key
  // leaves the stored value untouched (the endpoint would otherwise null it), explicit null clears it.
  requiresOptionIds?: Record<string, string[]> | null
}

interface JcConfiguratorFiltersState {
  configuratorId: string | null
  filters: JcConfiguratorFilter[]
  taxOptions: JcTaxOption[]
  loading: boolean
}

export const useJcConfiguratorFiltersStore = defineStore({
  id: "jc.configurator.filters",
  state: (): JcConfiguratorFiltersState => ({
    configuratorId: null,
    filters: [],
    taxOptions: [],
    loading: false,
  }),
  actions: {
    async fetchFilters(configuratorId: string): Promise<boolean> {
      if (!configuratorId) return false
      this.loading = true
      try {
        const url = CONSTANT.API.JC_FILTER.CONFIGURATOR_FILTER.LIST.replace("{configuratorId}", configuratorId)
        const result = (await httpService.get(url)) as ListResponse<JcConfiguratorFilter>
        this.filters = (result?.data?.data ?? []).slice().sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
        this.configuratorId = configuratorId
        return true
      } catch (error) {
        console.error("Failed to fetch configurator filters", error)
        this.filters = []
        return false
      } finally {
        this.loading = false
      }
    },

    async createFilter(configuratorId: string, payload: ConfiguratorFilterCreatePayload): Promise<string | null> {
      try {
        const url = CONSTANT.API.JC_FILTER.CONFIGURATOR_FILTER.CREATE.replace("{configuratorId}", configuratorId)
        const result = (await httpService.post(url, payload)) as { data: { id: string } }
        await this.fetchFilters(configuratorId)
        return result?.data?.id ?? null
      } catch (error) {
        console.error("Failed to create configurator filter", error)
        return null
      }
    },

    async updateFilter(configuratorId: string, id: string, payload: ConfiguratorFilterUpdatePayload): Promise<boolean> {
      try {
        const url = CONSTANT.API.JC_FILTER.CONFIGURATOR_FILTER.UPDATE.replace("{configuratorId}", configuratorId).replace("{id}", id)
        await httpService.patch(url, payload)
        return true
      } catch (error) {
        console.error("Failed to update configurator filter", error)
        return false
      }
    },

    async deleteFilter(configuratorId: string, id: string): Promise<boolean> {
      try {
        const url = CONSTANT.API.JC_FILTER.CONFIGURATOR_FILTER.DELETE.replace("{configuratorId}", configuratorId).replace("{id}", id)
        await httpService.delete(url)
        await this.fetchFilters(configuratorId)
        return true
      } catch (error) {
        console.error("Failed to delete configurator filter", error)
        return false
      }
    },

    async upsertOptionSetting(configuratorId: string, configuratorFilterId: string, payload: OptionSettingPayload): Promise<boolean> {
      try {
        const url = CONSTANT.API.JC_FILTER.CONFIGURATOR_FILTER.OPTION_SETTING.replace("{configuratorId}", configuratorId).replace("{id}", configuratorFilterId)
        await httpService.put(url, payload)
        return true
      } catch (error) {
        console.error("Failed to upsert option setting", error)
        return false
      }
    },

    async reorder(configuratorId: string, orderedIds: string[]): Promise<boolean> {
      try {
        const url = CONSTANT.API.JC_FILTER.CONFIGURATOR_FILTER.REORDER.replace("{configuratorId}", configuratorId)
        await httpService.patch(url, { order: orderedIds })
        await this.fetchFilters(configuratorId)
        return true
      } catch (error) {
        console.error("Failed to reorder configurator filters", error)
        await this.fetchFilters(configuratorId)
        return false
      }
    },

    // Manufacturers assigned to a filter option — the ones eligible for a
    // per-manufacturer set price. Not cached: the set depends on the option.
    async fetchFilterOptionManufacturers(filterOptionId: string): Promise<JcOptionManufacturer[]> {
      if (!filterOptionId) return []
      try {
        const url = CONSTANT.API.JC_FILTER.FILTER_OPTION.MANUFACTURERS.replace("{filterOptionId}", filterOptionId)
        const result = (await httpService.get(url)) as { data?: { data?: JcOptionManufacturer[] } }
        return result?.data?.data ?? []
      } catch (error) {
        console.error("Failed to fetch filter-option manufacturers", error)
        return []
      }
    },

    async fetchTaxOptions(): Promise<void> {
      // Cached: tax rates rarely change within a session.
      if (this.taxOptions.length > 0) return
      try {
        const result = (await httpService.get(CONSTANT.API.JC_FILTER.CONFIGURATOR_FILTER.TAX_OPTIONS)) as ListResponse<JcTaxOption>
        this.taxOptions = result?.data?.data ?? []
      } catch (error) {
        console.error("Failed to fetch tax options", error)
        this.taxOptions = []
      }
    },

    reset() {
      this.configuratorId = null
      this.filters = []
    },
  },
})
